import { useState } from 'react';
import { FileCheck, Pencil, Plus, Trash2 } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useGetConsentTemplates from '@/features/dashboard/consentTemplates/hooks/useGetConsentTemplates.ts';
import useDeleteConsentTemplate from '@/features/dashboard/consentTemplates/hooks/useDeleteConsentTemplate.ts';
import { useConfirmationDialogContext } from '@/components/ui/ConfirmDialog.Context.tsx';
import {
  SectionLayout,
  SectionLayoutSkeleton,
} from '@/components/ui/SectionLayout.tsx';
import {
  SettingsList,
  SettingsListRow,
} from '@/components/ui/SettingsList.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import Chip from '@/components/ui/Chip.tsx';
import CreateConsentTemplateModal from '@/features/dashboard/consentTemplates/components/CreateConsentTemplateModal.tsx';
import EditConsentTemplateModal from '@/features/dashboard/consentTemplates/components/EditConsentTemplateModal.tsx';
import { getConsentTypeLabel } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.utils.ts';

const ConsentTemplatesList = () => {
  const { data: templates, isLoading } = useGetConsentTemplates();
  const deleteConsentTemplate = useDeleteConsentTemplate();
  const { showDialog } = useConfirmationDialogContext();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    const confirmed = await showDialog({
      title: 'Usuń szablon zgody',
      description: 'Czy na pewno chcesz usunąć ten szablon zgody?',
      variant: 'destructive',
      confirmText: 'Usuń',
    });
    if (confirmed) {
      deleteConsentTemplate.mutate(id);
    }
  };

  if (isLoading) {
    return <SectionLayoutSkeleton rows={3} />;
  }

  const hasTemplates = !!templates && templates.length > 0;

  return (
    <>
      <SectionLayout
        title="Szablony zgód"
        description="Każda edycja treści tworzy nową wersję i archiwizuje poprzednią. Wyjazdy trzymają się wersji przypiętej w dniu zapisu."
      >
        {hasTemplates ? (
          <>
            <SettingsList>
              {templates.map((template) => (
                <SettingsListRow
                  key={template.id}
                  title={template.name}
                  badge={
                    <Chip
                      label={getConsentTypeLabel(template.type)}
                      size="sm"
                      className="shrink-0"
                    />
                  }
                  subtitle={`Wersja ${template.version}`}
                  actions={
                    <>
                      <ActionButton
                        tone="outline"
                        size="toolbar"
                        onClick={() => setEditingVersionId(template.id)}
                      >
                        <Pencil />
                        Zmień
                      </ActionButton>
                      <ActionButton
                        tone="danger"
                        size="toolbar"
                        iconOnly
                        title="Usuń szablon"
                        onClick={() => handleDelete(template.id)}
                      >
                        <Trash2 />
                      </ActionButton>
                    </>
                  }
                />
              ))}
            </SettingsList>

            <ActionButton
              tone="outline"
              size="toolbar"
              className="mt-3"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Plus />
              Dodaj szablon
            </ActionButton>
          </>
        ) : (
          <NoItems
            icon={<FileCheck />}
            title="Nie masz jeszcze szablonów zgód"
            description="Dodaj zgodę, aby przypiąć ją do wyjazdu i zbierać akceptacje przy zapisie."
          >
            <ActionButton
              tone="outline"
              size="toolbar"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Plus />
              Dodaj szablon
            </ActionButton>
          </NoItems>
        )}
      </SectionLayout>

      {isCreateModalOpen && (
        <CreateConsentTemplateModal
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}

      {editingVersionId && (
        <EditConsentTemplateModal
          versionId={editingVersionId}
          onClose={() => setEditingVersionId(null)}
        />
      )}
    </>
  );
};

export default ConsentTemplatesList;
